import { DateTime } from "luxon";
import { CycleRequirements } from "@/types";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import { formatStatus } from "@/features/person/utils/utils";
import { toFormat } from "@/lib/dateHelpers";
import {
  getGateEligibilitySubtext,
  getGateStatusLabel,
  isGateRequirementsView,
  resolveGateCohortCode,
  resolveGateSatisfied
} from "@/pages/people/[pgaId]/education/professional-development/pdrGateHelpers";

export const PDR_WARNING_WINDOW_MONTHS = 6;
export const PDR_PACE_TOLERANCE_MONTHS = 2;

export type PdrPaceStatus = "Ahead of Pace" | "Behind Pace" | "On Pace" | "Complete" | undefined;

export type PdrBucket = { label: string; earned: number; required: number };

export function isTotalBucket(label: string) {
  return label.toLowerCase().includes("total");
}

export function getPdrBuckets(cycleRequirements: CycleRequirements): PdrBucket[] {
  return cycleRequirements.requirements
    .map((requirement) => ({
      label: requirement.label,
      earned: requirement.earned,
      required: requirement.totalRequirement
    }))
    .sort((a, b) => {
      if (isTotalBucket(a.label) && !isTotalBucket(b.label)) return 1;
      if (!isTotalBucket(a.label) && isTotalBucket(b.label)) return -1;
      return 0;
    });
}

export function getPdrPaceTotals(buckets: PdrBucket[]) {
  // Sub-buckets (e.g. PGA Required) aren't additive with the Total bucket —
  // they're composition constraints on it: of the X total PDRs required, at
  // least Y must come from a specific category. So pace is computed against
  // the Total bucket's required count when present, but reduced by any unmet
  // sub-bucket gap so overage in one category can't paper over a shortfall in
  // another.
  //
  // effectiveEarned = min(totalEarned, totalRequired) − sum of unmet sub-gaps
  //
  // When no Total bucket is sent we fall back to summing every bucket, which
  // is the right behavior for cycles that only define category requirements.
  const totalBucket = buckets.find((bucket) => isTotalBucket(bucket.label));
  const subBuckets = buckets.filter((bucket) => !isTotalBucket(bucket.label));

  if (!totalBucket) {
    const earned = subBuckets.reduce((sum, bucket) => sum + bucket.earned, 0);
    const required = subBuckets.reduce((sum, bucket) => sum + bucket.required, 0);
    return { earned, required };
  }

  const required = totalBucket.required;
  const subGap = subBuckets.reduce((sum, bucket) => sum + Math.max(0, bucket.required - bucket.earned), 0);
  const earned = Math.max(0, Math.min(totalBucket.earned, required) - subGap);

  return { earned, required };
}

export function isPdrCycleInWarningWindow(cycleEndsOn: string) {
  const cycleEnd = DateTime.fromISO(cycleEndsOn).startOf("day");

  return cycleEnd.isValid && cycleEnd <= DateTime.now().startOf("day").plus({ months: PDR_WARNING_WINDOW_MONTHS });
}

export function getPdrPaceStatus(
  cycleRequirements: CycleRequirements,
  earned: number,
  required: number
): PdrPaceStatus {
  if (required <= 0) {
    return undefined;
  }

  // The backend's `requirements_met` status is the only signal that every
  // bucket (Total, PGA Required, etc.) is satisfied. We trust it over the
  // earned/required math in case the backend applies cycle-end grace periods
  // or other rules we don't model on the client.
  if (cycleRequirements.status?.toLowerCase() === "requirements_met") {
    return "Complete";
  }

  const cycleStart = DateTime.fromISO(cycleRequirements.cycleStartsOn).startOf("day");
  const cycleEnd = DateTime.fromISO(cycleRequirements.cycleEndsOn).startOf("day");
  const today = DateTime.now().startOf("day");
  const cycleDuration = cycleEnd.diff(cycleStart, "days").days;

  if (!cycleStart.isValid || !cycleEnd.isValid || cycleDuration <= 0) {
    return undefined;
  }

  const getExpectedEarnedForDate = (date: DateTime) => {
    const elapsedDuration = Math.min(Math.max(date.diff(cycleStart, "days").days, 0), cycleDuration);
    return required * (elapsedDuration / cycleDuration);
  };

  if (!isPdrCycleInWarningWindow(cycleRequirements.cycleEndsOn)) {
    const minimumOnPaceEarned = getExpectedEarnedForDate(today.minus({ months: PDR_PACE_TOLERANCE_MONTHS }));
    const maximumOnPaceEarned = getExpectedEarnedForDate(today.plus({ months: PDR_PACE_TOLERANCE_MONTHS }));

    if (earned < minimumOnPaceEarned) {
      return "Behind Pace";
    }

    return earned > maximumOnPaceEarned ? "Ahead of Pace" : "On Pace";
  }

  const expectedEarned = getExpectedEarnedForDate(today);

  if (earned > expectedEarned) {
    return "Ahead of Pace";
  }

  return earned === expectedEarned ? "On Pace" : "Behind Pace";
}

export function buildPdrPaceTooltip(args: {
  paceStatus: PdrPaceStatus;
  status: string | undefined;
  cycleRequirements?: CycleRequirements;
}) {
  const { paceStatus, status, cycleRequirements } = args;
  const gateCohortCode = cycleRequirements ? resolveGateCohortCode(cycleRequirements.cohortCode) : undefined;

  if (gateCohortCode) {
    const gateSatisfied = resolveGateSatisfied({
      status: cycleRequirements?.status,
      gateObligation: null
    });

    return {
      title: `PDR Status: ${getGateStatusLabel(gateCohortCode, gateSatisfied)}`,
      supportingText: getGateEligibilitySubtext(gateCohortCode, gateSatisfied)
    };
  }

  if (paceStatus === "Complete") {
    return {
      title: "PDR Status: Complete",
      supportingText: "All PDR requirements for this cycle have been met."
    };
  }

  if (status?.toLowerCase() === "recovering") {
    return {
      title: "PDR Status: Recovering",
      supportingText:
        "Member carried a deficit forward from a previous cycle. They must reach the recovery threshold in every category before they can be moved out of Class F and resume normal cycle progress."
    };
  }

  if (paceStatus === "Ahead of Pace" || paceStatus === "Behind Pace" || paceStatus === "On Pace") {
    return {
      title: `PDR Status: ${paceStatus}`,
      supportingText:
        "Measures earned PDRs against expected progress for today's point in the cycle. Unmet sub-requirements (e.g. PGA Required) count against the total."
    };
  }

  // Pace couldn't be computed (e.g. no cycle requirements, or missing/invalid
  // cycle dates). Surface the backend status anyway so the tooltip stays
  // useful.
  if (status?.toLowerCase() === "requirements_not_met") {
    return {
      title: "PDR Status: Requirements Not Met",
      supportingText:
        "Cycle requirements have not been met and pace can't be shown for this cycle. Check the PDR detail page for category-level credit history."
    };
  }

  if (status) {
    return {
      title: `PDR Status: ${formatStatus(status)}`,
      supportingText: "Check the PDR detail page for the full cycle picture."
    };
  }

  return undefined;
}

export function buildPdrMetric(cycleRequirements: CycleRequirements, pgaId: string | undefined): MetricCardType<Path> {
  const status = cycleRequirements.status?.toLowerCase();
  const gateCohortCode = resolveGateCohortCode(cycleRequirements.cohortCode);
  const isGateView = isGateRequirementsView(cycleRequirements.cohortCode);
  const gateSatisfied = resolveGateSatisfied({ status: cycleRequirements.status, gateObligation: null });
  const buckets = getPdrBuckets(cycleRequirements);
  const { earned, required } = getPdrPaceTotals(buckets);
  const paceStatus = getPdrPaceStatus(cycleRequirements, earned, required);
  const inWarningWindow = isPdrCycleInWarningWindow(cycleRequirements.cycleEndsOn);
  const hasUnmetBucket = buckets.some((bucket) => bucket.required > 0 && bucket.earned < bucket.required);

  let variant: "success" | "warn" | "error" | undefined;
  if (isGateView && gateCohortCode) {
    variant = gateSatisfied ? "success" : "warn";
  } else if (status === "requirements_met") {
    variant = "success";
  } else if (status === "recovering") {
    variant = "error";
  } else if (inWarningWindow && (status === "requirements_not_met" || hasUnmetBucket)) {
    // Warn inside the final six months whenever any individual bucket is
    // short, even if the backend hasn't flipped the cycle status yet (e.g.
    // Total PDRs is met but PGA Required still has a deficit).
    variant = "warn";
  }

  const valueDetail =
    isGateView && gateCohortCode
      ? getGateStatusLabel(gateCohortCode, gateSatisfied)
      : required > 0
        ? (paceStatus ?? formatStatus(cycleRequirements.status))
        : formatStatus(cycleRequirements.status, "No Requirements");
  const value = valueDetail ? `PDRs - ${valueDetail}` : "PDRs";

  const cycleEndDateFormatted = toFormat(cycleRequirements.cycleEndsOn);
  const cycleLine = cycleEndDateFormatted
    ? `Cycle ${cycleRequirements.cycleCode} \u00B7 Ends ${cycleEndDateFormatted}`
    : `Cycle ${cycleRequirements.cycleCode}`;
  // Hide bucket lines entirely when this cycle has no requirements at all —
  // rendering "0 of 0 Total PDRs" reads as a data problem rather than the
  // intended "nothing required this cycle" signal.
  const bucketLines =
    required > 0 ? buckets.map((bucket) => `${bucket.earned} of ${bucket.required} ${bucket.label}`) : [];
  const label = [cycleLine, ...bucketLines].join("\n");

  const tooltip = buildPdrPaceTooltip({
    paceStatus,
    status: cycleRequirements.status,
    cycleRequirements
  });

  return {
    label,
    value,
    iconName: "school",
    ...(variant && { variant }),
    ...(tooltip && { tooltip }),
    ...(pgaId && {
      link: {
        to: "/people/:pgaId/education/professional-development",
        params: { pgaId }
      }
    })
  };
}
